import type { MouseEvent } from 'react';
import { message } from 'antd';
import { t } from 'i18next';
import { GetDocumentReadLink } from './apiHelper';
import { withQueryParam } from './urlHelper';

type DocumentStatus =
  | string
  | number
  | null
  | undefined
  | Promise<string | number | null | undefined>;

/**
 * The links to the documents expire after a short time, so the link which came with the data of the page may be
 * too old when the user opens the document. Asks the API for a new link and opens it in a new tab.
 * The tab is opened first, while the browser still counts it as the result of the click, otherwise it is blocked.
 */
export async function openDocument(
  documentUrl: string,
  status?: DocumentStatus
): Promise<void> {
  if (!documentUrl) {
    return;
  }

  const tab = window.open('', '_blank');
  if (tab) {
    tab.opener = null;
  }

  try {
    const readLink = await GetDocumentReadLink(documentUrl);
    const documentStatus = await status;
    const link =
      documentStatus === undefined
        ? readLink
        : withQueryParam(readLink, 'status', documentStatus);

    if (tab) {
      tab.location.href = link;
    } else {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  } catch (error) {
    tab?.close();
    console.error(error);
    message.error(t('documentAccess.openError'));
  }
}

/**
 * Click handler for a link which has the (possibly expired) document link as its href:
 * a normal click opens a new link, the href stays for the keyboard and the browser's own link menu.
 */
export function openDocumentOnClick(
  documentUrl: string,
  status?: DocumentStatus
): (event: MouseEvent) => void {
  return (event) => {
    event.preventDefault();
    void openDocument(documentUrl, status);
  };
}
